import { PointsType } from "./gradingSchema";

interface ScoreGrade {
  score: number;
  grade: number;
}

/**
 * This function calculates the grade based on the points achieved, maximum points, nTerm and linear percentage.
 * see: https://support.test-correct.nl/knowledge/normeren#n-term
 */
export function calculateGrade(
  pointsAchieved: number,
  maximumPoints: number,
  nTerm: number,
  linearPercentage: number,
  passingGrade: number = 5.5,
) {
  const normalizedNterm = nTerm / 100;
  const normalizedLinearPercentage = linearPercentage / 100;

  // Formula for initial q and r values based on passingGrade
  let q = 11 - passingGrade;
  let r = passingGrade - 2;

  // Apply nTerm adjustment
  q = q - normalizedNterm;
  r = r + normalizedNterm;

  const cg = maximumPoints * normalizedLinearPercentage;
  const z = (maximumPoints - cg) / q;

  if (pointsAchieved > cg) {
    const result = 10 - (maximumPoints - pointsAchieved) / z;
    const roundedResult = Math.round(result * 10) / 10;
    return roundedResult;
  }

  const result = 1 + (pointsAchieved / cg) * r;
  const roundedResult = Math.round(result * 10) / 10;
  return roundedResult;
}

export function generateGradingTable(
  maxScore: number,
  term: number,
  linearPercentage: number,
  pointsType: PointsType,
  passingGrade: number = 5.5,
): ScoreGrade[] {
  const constrainedMaxScore = Math.min(maxScore, 300);

  let multiplier = 1;
  if (pointsType === "half") {
    multiplier = 2;
  } else if (pointsType === "quarter") {
    multiplier = 4;
  } else if (pointsType === "tenth") {
    multiplier = 10;
  }

  const scores = Array.from(
    { length: constrainedMaxScore * multiplier + 1 },
    (_, i) => i / multiplier,
  );
  return scores.map((score) => {
    const grade = calculateGrade(
      score,
      constrainedMaxScore,
      term,
      linearPercentage,
      passingGrade,
    );

    return {
      score,
      grade,
    };
  });
}
